// Sprint ONBOARD ONB.C1 (A.3) — shared setup-task display copy.
//
// Promoted out of OnboardingPanel.tsx so the dashboard panel AND the focused
// /setup SetupWizard render identical task copy (no drift). The wizard needs
// the extra `description` + `capabilitiesUnlocked` fields for its inline task
// expansion (ONB-C1.md AC); the panel ignores them.
//
// Trust boundary (carried verbatim from OnboardingPanel): ctaPath values are
// STATIC LITERALS only — never interpolate server data into a CTA URL.

export type SetupTaskType =
  | 'upload_logo'
  | 'invite_technical_lead'
  | 'connect_github'
  | 'authorize_gdrive'
  | 'choose_export_folder'
  | 'configure_sso'
  | 'connect_slack'
  | 'create_first_project'
  | 'pass_first_phase_gate';

export type SetupTaskStatus = 'pending' | 'in_progress' | 'blocked' | 'completed' | 'skipped';

export interface SetupTaskCopy {
  title:                string;
  verb:                 string;
  /**
   * Navigation target for the task's CTA. OPTIONAL (#1247 follow-on): a task
   * whose action is handled in-place (a modal) has no path — today that's
   * invite_technical_lead, which opens the shared InviteTechnicalLeadModal in
   * both the dashboard panel and the /setup hub. Consumers MUST guard the
   * navigate (`if (ctaPath) navigate(ctaPath)`). Static literals only — never
   * interpolate server data into a CTA URL.
   */
  ctaPath?:             string;
  ctaLabel:             string;
  /** Shown in the SetupWizard inline-expanded task card. */
  description:          string;
  /** Bullet list of capabilities the task unlocks (SetupWizard expanded card). */
  capabilitiesUnlocked: string[];
}

export const TASK_COPY: Record<SetupTaskType, SetupTaskCopy> = {
  upload_logo: {
    title: 'Upload your organisation logo', verb: 'uploading logo',
    ctaPath: '/settings/organisation', ctaLabel: 'Upload logo',
    description: 'Add your organisation logo so BluePrint-generated documents and dashboards carry your branding.',
    capabilitiesUnlocked: ['Branded artefacts and exports', 'Recognisable client-facing reports'],
  },
  invite_technical_lead: {
    title: 'Invite your Technical Lead', verb: 'inviting Tech Lead',
    // No ctaPath (#1247 follow-on): the CTA opens the shared
    // InviteTechnicalLeadModal in-place (dashboard panel + /setup hub), never
    // navigates. Was '/settings/organisation' — the generic team-invite modal
    // that never assigned technical_lead_user_id (the bug this fixes).
    ctaLabel: 'Send invite',
    description: 'Invite the engineer who will connect BluePrint to your tools. They will receive a focused setup link.',
    capabilitiesUnlocked: ['Delegated technical setup', 'GitHub and Drive integration by the right person'],
  },
  connect_github: {
    title: 'Connect GitHub', verb: 'connecting GitHub',
    // No ctaPath (#1274 F4 / #1277): the CTA opens the shared
    // SetupGithubConnectModal in-place (dashboard panel + /setup hub), which
    // POSTs to the SETUP-SCOPED /setup-project/connect-github endpoint. This
    // supersedes F2's interim reroute to the admin-only /settings/organisation:
    // the modal works for BOTH the admin (self-handling Buyer) AND the assigned
    // TL (TENANT_MEMBER), who the admin Settings control would 403. #830 builds
    // the polished GitHub-App sub-wizard later.
    ctaLabel: 'Connect',
    description: 'Authorise BluePrint to access your GitHub organisation so it can review code and analyse pull requests.',
    capabilitiesUnlocked: ['Automated code review', 'PR analysis and traceability'],
  },
  authorize_gdrive: {
    title: 'Authorise Google Drive', verb: 'authorising Drive',
    // ONB.C1: routes into the /setup sub-wizard owned by A.5 (ONB.C3).
    ctaPath: '/setup/authorize-gdrive', ctaLabel: 'Authorise',
    description: 'Authorise BluePrint to read and write documents in Google Drive so it can import and export deliverables.',
    capabilitiesUnlocked: ['Document import from Drive', 'Deliverable export to Drive'],
  },
  choose_export_folder: {
    title: 'Choose your export folder', verb: 'choosing folder',
    ctaPath: '/setup/authorize-gdrive', ctaLabel: 'Choose folder',
    description: 'Pick the Drive folder BluePrint writes exported documents into. Available once Drive is authorised.',
    capabilitiesUnlocked: ['A consistent home for exported artefacts'],
  },
  configure_sso: {
    title: 'Configure SSO', verb: 'configuring SSO',
    ctaPath: '/settings/organisation', ctaLabel: 'Configure',
    description: 'Set up single sign-on so your team signs in with your existing identity provider.',
    capabilitiesUnlocked: ['One-click team sign-in', 'Centralised access control'],
  },
  connect_slack: {
    title: 'Connect Slack', verb: 'connecting Slack',
    // #1274 — the real Slack control lives at /settings ("Slack Notifications",
    // SettingsPage.tsx), NOT /settings/organisation (which has no Slack control).
    // Reroute so the CTA reaches the working surface. (Skip-allowed for growth,
    // so this is a routing improvement, not a hard-wall fix.)
    ctaPath: '/settings', ctaLabel: 'Connect',
    description: 'Connect Slack so BluePrint can post approvals, gate alerts, and status updates to your channels.',
    capabilitiesUnlocked: ['In-Slack approvals and alerts', 'Team-wide status visibility'],
  },
  create_first_project: {
    title: 'Create your first project', verb: 'creating project',
    ctaPath: '/projects', ctaLabel: 'Create project',
    description: 'Spin up your first BluePrint project to start turning requirements into delivery.',
    capabilitiesUnlocked: ['Your first delivery workspace'],
  },
  pass_first_phase_gate: {
    title: 'Pass your first phase gate', verb: 'passing phase gate',
    ctaPath: '/projects', ctaLabel: 'Open projects',
    description: 'Complete and approve your first phase gate to confirm BluePrint is delivering for your team.',
    capabilitiesUnlocked: ['Validated end-to-end delivery flow'],
  },
};

export const PHASE_LABELS: Record<0 | 1 | 2 | 3, string> = {
  0: 'Branding & Identity',
  1: 'Team Setup',
  2: 'Storage Integration',
  3: 'Activation',
};

export const STATUS_PILL: Record<SetupTaskStatus, { label: string; bg: string; fg: string }> = {
  pending:     { label: 'Pending',     bg: 'rgba(124, 58, 237, 0.10)', fg: '#7c3aed' },
  in_progress: { label: 'In progress', bg: 'rgba(245, 158, 11, 0.15)', fg: '#d97706' },
  blocked:     { label: 'Blocked',     bg: 'rgba(239, 68, 68, 0.15)',  fg: '#ef4444' },
  completed:   { label: 'Done',        bg: 'rgba(34, 197, 94, 0.15)',  fg: '#16a34a' },
  skipped:     { label: 'Skipped',     bg: 'rgba(107, 114, 128, 0.15)', fg: '#6b7280' },
};
